import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider } from "@/lib/auth";
import { scrollToId } from "@/lib/site";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import InfoStrip from "@/components/InfoStrip";
import TopBanners from "@/components/TopBanners";
import ShabbatWidget from "@/components/ShabbatWidget";
import Worship from "@/components/Worship";
import Events from "@/components/Events";
import Membership from "@/components/Membership";
import ExploreTeasers from "@/components/ExploreTeasers";
import Footer from "@/components/Footer";
import WelcomePopup from "@/components/WelcomePopup";

const Login = lazy(() => import("@/pages/Login"));
const Admin = lazy(() => import("@/pages/Admin"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const RsvpPrint = lazy(() => import("@/pages/RsvpPrint"));
const AttendancePrint = lazy(() => import("@/pages/AttendancePrint"));
const YahrzeitPrint = lazy(() => import("@/pages/YahrzeitPrint"));
const DirectoryPage = lazy(() => import("@/pages/DirectoryPage"));
const MembersPage = lazy(() => import("@/pages/MembersPage"));
const HhdPage = lazy(() => import("@/pages/HhdPage"));
const LivePage = lazy(() => import("@/pages/LivePage"));
const Bulletins = lazy(() => import("@/pages/Bulletins"));
const VideosPage = lazy(() => import("@/pages/VideosPage"));
const KidsPage = lazy(() => import("@/pages/KidsPage"));
const SchmoozePage = lazy(() => import("@/pages/SchmoozePage"));
const LearningPage = lazy(() => import("@/pages/LearningPage"));
const CalendarPage = lazy(() => import("@/pages/CalendarPage"));
const CheckIn = lazy(() => import("@/pages/CheckIn"));
const Account = lazy(() => import("@/pages/Account"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("@/pages/PaymentCancel"));
const ChallahBakePage = lazy(() => import("@/pages/ChallahBakePage"));
const HavanaPage = lazy(() => import("@/pages/HavanaPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const CalendarPrint = lazy(() => import("@/pages/CalendarPrint"));
const RabbiPage = lazy(() => import("@/pages/RabbiPage"));
const GroupsPage = lazy(() => import("@/pages/GroupsPage"));
const MemoriesPage = lazy(() => import("@/pages/MemoriesPage"));
const NewsPage = lazy(() => import("@/pages/NewsPage"));
const StoryPage = lazy(() => import("@/pages/StoryPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const MensClubPage = lazy(() => import("@/pages/MensClubPage"));
const WomensLeaguePage = lazy(() => import("@/pages/WomensLeaguePage"));
const UpcomingPage = lazy(() => import("@/pages/UpcomingPage"));

// Sections that moved off the homepage → their new pages (old links keep working)
const HASH_MOVES = {
  "#contact": "/contact",
  "#rabbi": "/rabbi",
  "#community": "/groups",
  "#memories": "/memories",
  "#news": "/news",
  "#about": "/story",
  "#story": "/story",
  "#leadership": "/story#leadership",
  "#yjpn": "/groups#yjpn",
  "#lifecycle": "/contact#lifecycle",
};

function HomePage() {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const target = HASH_MOVES[location.hash];
    if (target) {
      navigate(target, { replace: true });
    } else if (location.hash) {
      const t = setTimeout(() => scrollToId(location.hash), 400);
      return () => clearTimeout(t);
    }
  }, [location.hash, navigate]);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.2, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="grain min-h-screen bg-ink text-bone overflow-x-clip">
      <Navbar />
      <main>
        <TopBanners />
        <Hero />
        <InfoStrip />
        <ShabbatWidget />
        <Worship />
        <Events />
        <ExploreTeasers />
        <Membership />
      </main>
      <Footer />
      <WelcomePopup />
    </div>
  );
}

// Old bethelwpb.com (ShulCloud) URLs → their new homes, now that the domain serves this app
const LEGACY_REDIRECTS = [
  [/^\/hhd5787/i, "/hhd"],
  [/^\/form\/learning/i, "/learning"],
  [/^\/form\/membership/i, "/#join"],
  [/^\/(join-us|join|membership)\/?$/i, "/#join"],
  // High Holy Days campaign: any other old ShulCloud form link goes to HHD registration (paid ads are pointing here)
  [/^\/form\//i, "/hhd"],
  [/^\/event\/(stretch|little-and-loved|totshabbat|toddlerps|relschool|batmitzvah)/i, "/kids"],
  [/^\/event\/(hebrew|socialmedia)/i, "/learning"],
  [/^\/event\//i, "/#events"],
  [/^\/youth/i, "/kids"],
  [/^\/100\//, "/memories"],
  [/^\/cal/i, "/calendar"],
  [/^\/fridaynight/i, "/#events"],
  [/^\/mensclub/i, "/mens-club"],
  [/^\/womensleague/i, "/womens-league"],
  [/^\/worship\/links/i, "/live"],
  [/^\/centennial/i, "/story"],
];

function DonateRedirect() {
  useEffect(() => {
    window.location.replace("https://templebethelwpb.shulcloud.com/payment.php");
  }, []);
  return <div className="min-h-screen bg-ink" data-testid="donate-redirect" />;
}

function LegacyOrNotFound() {
  const location = useLocation();
  const navigate = useNavigate();
  // ShulCloud HHD form links (old domain paths) pass straight through to the live ShulCloud forms
  const shulcloudForm = /^\/form\/(hhd|kolnidre|havana|centennial)/i.test(location.pathname);
  const target = shulcloudForm ? null : LEGACY_REDIRECTS.find(([re]) => re.test(location.pathname))?.[1];
  useEffect(() => {
    if (shulcloudForm) {
      window.location.replace(`https://templebethelwpb.shulcloud.com${location.pathname}`);
      return;
    }
    if (!target) return;
    if (target.includes("#")) {
      const [path, hash] = target.split("#");
      navigate(path || "/");
      setTimeout(() => scrollToId(`#${hash}`), 600);
    } else {
      navigate(target);
    }
  }, [target, navigate, shulcloudForm, location.pathname]);
  if (target || shulcloudForm) return <div className="min-h-screen bg-ink" data-testid="legacy-redirect" />;
  return (
    <div className="grain min-h-screen bg-ink text-bone flex items-center" data-testid="not-found-page">
      <div className="max-w-xl mx-auto px-6 text-center py-32">
        <p className="text-[10px] tracking-[0.35em] uppercase text-gold mb-6">404 · Page Not Found</p>
        <h1 className="font-syne font-extrabold uppercase tracking-tighter leading-[0.95] text-4xl md:text-5xl mb-6">
          Lost? Even wandering Jews <span className="text-gold">find their way home.</span>
        </h1>
        <p className="font-cormorant italic text-xl text-foreground/60 mb-10">
          This page moved with us to our new home. Let's get you back.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <a href="/" data-testid="notfound-home-link" className="px-8 py-4 bg-gold text-inkdeep text-xs tracking-[0.25em] uppercase font-medium hover:bg-gold-light transition-colors">Back Home</a>
          <a href="/#events" data-testid="notfound-events-link" className="px-8 py-4 border border-white/20 text-xs tracking-[0.25em] uppercase hover:border-gold hover:text-gold transition-colors">See Events</a>
        </div>
      </div>
    </div>
  );
}

const PageLoader = () => (
  <div className="min-h-screen bg-ink flex items-center justify-center" data-testid="page-loader">
    <div className="w-8 h-8 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
  </div>
);

const Sub = ({ children }) => (
  <>
    <Navbar sticky />
    {children}
  </>
);

function AppRouter() {
  const location = useLocation(); // hash read here reactively — not window.location.hash
  if (location.hash?.includes("session_id=")) {
    return (
      <Suspense fallback={<PageLoader />}>
        <AuthCallback />
      </Suspense>
    );
  }
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/admin/print/:eventId" element={<RsvpPrint />} />
        <Route path="/admin/attendance/:eventId" element={<AttendancePrint />} />
        <Route path="/admin/print-yahrzeits" element={<YahrzeitPrint />} />
        <Route path="/directory" element={<Sub><DirectoryPage /></Sub>} />
        <Route path="/members" element={<Sub><MembersPage /></Sub>} />
        <Route path="/hhd" element={<Sub><HhdPage /></Sub>} />
        <Route path="/high-holy-days" element={<Navigate to="/hhd" replace />} />
        <Route path="/challah-bake" element={<Sub><ChallahBakePage /></Sub>} />
        <Route path="/havana" element={<Sub><HavanaPage /></Sub>} />
        <Route path="/live" element={<Sub><LivePage /></Sub>} />
        <Route path="/bulletins" element={<Sub><Bulletins /></Sub>} />
        <Route path="/videos" element={<Sub><VideosPage /></Sub>} />
        <Route path="/kids" element={<Sub><KidsPage /></Sub>} />
        <Route path="/schmooze" element={<Sub><SchmoozePage /></Sub>} />
        <Route path="/learning" element={<Sub><LearningPage /></Sub>} />
        <Route path="/calendar" element={<Sub><CalendarPage /></Sub>} />
        <Route path="/calendar/print" element={<CalendarPrint />} />
        <Route path="/privacy" element={<Sub><PrivacyPage /></Sub>} />
        <Route path="/rabbi" element={<Sub><RabbiPage /></Sub>} />
        <Route path="/groups" element={<Sub><GroupsPage /></Sub>} />
        <Route path="/memories" element={<Sub><MemoriesPage /></Sub>} />
        <Route path="/news" element={<Sub><NewsPage /></Sub>} />
        <Route path="/story" element={<Sub><StoryPage /></Sub>} />
        <Route path="/contact" element={<Sub><ContactPage /></Sub>} />
        <Route path="/mens-club" element={<Sub><MensClubPage /></Sub>} />
        <Route path="/womens-league" element={<Sub><WomensLeaguePage /></Sub>} />
        <Route path="/upcoming" element={<Sub><UpcomingPage /></Sub>} />
        <Route path="/admin/checkin/:eventId" element={<CheckIn />} />
        <Route path="/account" element={<Sub><Account /></Sub>} />
        <Route path="/donate" element={<DonateRedirect />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="*" element={<LegacyOrNotFound />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster theme="dark" position="bottom-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
